import { useNavigate } from 'react-router-dom';
import { useRef, useState } from 'react';
import { ArrowLeft, Ruler, Camera, Loader2, User } from 'lucide-react';
import { useUnits } from '../../lib/units';
import { useAuth } from '../../lib/auth-context';
import { api } from '../../lib/supabase';
import { toast } from 'sonner';

/**
 * Settings — measurement units, profile picture, and (in the future)
 * notifications + privacy. Accessed from the Profile screen.
 */
export default function Settings() {
  const nav = useNavigate();
  const { units, setUnits } = useUnits();
  const { profile, refreshProfile } = useAuth();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const avatarUrl = profile?.avatar_url;

  async function handleAvatarPicked(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please pick an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }
    setUploading(true);
    try {
      await api.uploadAvatar(file);
      toast.success('Profile picture updated');
      refreshProfile?.();
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  return (
    <div className="px-4 pt-4 pb-6 space-y-5" data-testid="settings-screen">
      <button onClick={() => nav(-1)} className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="settings-back">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div>
        <p className="rivl-label">Profile</p>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Settings</h1>
      </div>

      {/* Profile picture */}
      <section className="rivl-card p-4 space-y-3" data-testid="settings-avatar">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-md bg-secondary flex items-center justify-center">
            <Camera className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <div className="font-display font-semibold">Profile picture</div>
            <div className="text-xs text-muted-foreground">
              Shown next to your name and as your dot on the run map.
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div
            className="h-20 w-20 rounded-md border border-border bg-cover bg-center flex items-center justify-center bg-secondary"
            style={avatarUrl ? { backgroundImage: `url(${avatarUrl})` } : undefined}
            data-testid="settings-avatar-preview"
          >
            {!avatarUrl && <User className="h-8 w-8 text-muted-foreground" />}
          </div>

          <label className="flex-1">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarPicked}
              className="hidden"
              data-testid="settings-avatar-input"
              disabled={uploading}
            />
            <span
              role="button"
              className="inline-flex items-center justify-center w-full h-11 rounded-md border-2 border-dashed border-border text-xs font-semibold uppercase tracking-widest cursor-pointer hover:border-primary"
              data-testid="settings-avatar-pick"
            >
              {uploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Camera className="h-3.5 w-3.5 mr-2" />
                  {avatarUrl ? 'Change picture' : 'Upload picture'}
                </>
              )}
            </span>
            <p className="text-[10px] text-muted-foreground mt-1.5">JPG/PNG, up to 5 MB.</p>
          </label>
        </div>
      </section>

      {/* Units */}
      <section className="rivl-card p-4 space-y-3" data-testid="settings-units">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-md bg-secondary flex items-center justify-center">
            <Ruler className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <div className="font-display font-semibold">Measurement system</div>
            <div className="text-xs text-muted-foreground">
              Applied across the app — distance, pace, weight, height.
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setUnits('km')}
            data-testid="settings-unit-km"
            className={`h-14 rounded-md border-2 flex flex-col items-center justify-center transition-colors ${
              units === 'km' ? 'border-primary bg-primary/5 text-primary' : 'border-border'
            }`}
          >
            <span className="font-display font-bold">Metric</span>
            <span className="text-[9px] uppercase tracking-widest text-muted-foreground">km · kg · cm</span>
          </button>
          <button
            type="button"
            onClick={() => setUnits('mi')}
            data-testid="settings-unit-mi"
            className={`h-14 rounded-md border-2 flex flex-col items-center justify-center transition-colors ${
              units === 'mi' ? 'border-primary bg-primary/5 text-primary' : 'border-border'
            }`}
          >
            <span className="font-display font-bold">Imperial</span>
            <span className="text-[9px] uppercase tracking-widest text-muted-foreground">mi · lb · ft</span>
          </button>
        </div>
      </section>
    </div>
  );
}
